import axios from 'axios';
import { isTokenExpiringSoon, getRefreshToken, storeTokens, clearAuth } from './authManager';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(
  async (config) => {
    if (config.url.includes('/auth/')) {
      const token = localStorage.getItem('mc_admin_token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    if (isTokenExpiringSoon() && !isRefreshing) {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        isRefreshing = true;
        try {
          const response = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data;
          storeTokens(accessToken, newRefreshToken);
          onTokenRefreshed(accessToken);
          config.headers.Authorization = `Bearer ${accessToken}`;
        } catch (err) {
          clearAuth();
          window.location.href = '/login';
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }
    } else if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((newToken) => {
          config.headers.Authorization = `Bearer ${newToken}`;
          resolve(config);
        });
      });
    }

    const token = localStorage.getItem('mc_admin_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    if (err.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (refreshToken && !isRefreshing) {
        isRefreshing = true;
        try {
          const response = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data;
          storeTokens(accessToken, newRefreshToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          isRefreshing = false;
          return api(originalRequest);
        } catch (refreshErr) {
          isRefreshing = false;
          clearAuth();
          window.location.href = '/login';
          return Promise.reject(refreshErr);
        }
      } else {
        clearAuth();
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  verify2FA: (tempToken, code) =>
    api.post('/auth/verify-2fa', { code }, {
      headers: { Authorization: `Bearer ${tempToken}` }
    }),
  // @s11-logout-revokes (9 Oct 2026)
  // The backend already revokes properly - auth.controller.js logout runs
  //   if (refreshToken) UPDATE refresh_tokens SET revoked_at = NOW() WHERE token = $1
  // but it reads refreshToken from req.body, and this call sent no body at all.
  // So signing out cleared the browser and left a refresh token valid on the
  // server for its full 30 days. Nothing was wrong server-side; nobody asked.
  logout: () => api.post('/auth/logout', { refreshToken: getRefreshToken() }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  // @change-password-api-v2 logged-in user changes their own password
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  // @forgot-password-api forgot-password flow (Stage 6)
  requestPasswordReset: (email) =>
    api.post('/auth/request-password-reset', { email }),
  resetPassword: (email, code, newPassword) =>
    api.post('/auth/reset-password', { email, code, newPassword }),
};

export const dashboardAPI = {
  getStats: (params) => api.get('/admin/dashboard', { params }), // @period-picker-v1
  getTodayActivity: () => api.get('/admin/today'),
};

export const doctorAPI = {
  getAll: (params) => api.get('/admin/doctors', { params }),
  // /pending returns doctors where is_approved=false — i.e. doctors still in
  // the onboarding phase. Frontend displays them as "Onboarding doctors".
  getPending: () => api.get('/admin/doctors/pending'),
  create: (data) => api.post('/admin/doctors', data),
  getProfile: (doctorId) => api.get(`/admin/doctors/${doctorId}`),
  update: (doctorId, data) => api.patch(`/admin/doctors/${doctorId}`, data),
};

export const patientAPI = {
  getAll: (params) => api.get('/admin/patients', { params }),
  deactivate: (patientId) => api.patch(`/admin/patients/${patientId}/deactivate`),
  getProfile: (patientId) => api.get(`/admin/patients/${patientId}`),
};

export const rotaAPI = {
  getCalendar: (year, month) => api.get('/admin/rota', { params: { year, month } }),
  getPendingSlots: () => api.get('/admin/slots/pending'),
  editSlot: (slotId, data) => api.patch(`/admin/slots/${slotId}`, data),
  publishSlot: (slotId) => api.patch(`/admin/slots/${slotId}/publish`),
  rejectSlot: (slotId) => api.patch(`/admin/slots/${slotId}/reject`),
  publishAll: () => api.post('/admin/slots/publish-all'),
  addSlot: (data) => api.post('/admin/slots', data),
  addBulkSlots: (data) => api.post('/admin/slots/bulk', data),
  deleteSlot: (slotId) => api.delete(`/admin/slots/${slotId}`),
  // @admin-clear-day-v1 (30 Sep 2026) -- M-40. The preview changes nothing.
  // clearDay must carry expectedAppointments from that preview, or the
  // backend refuses with 409 STALE_PREVIEW rather than cancelling a booking
  // nobody has seen.
  clearDayPreview: (doctorId, date) => api.get(`/admin/doctors/${doctorId}/clear-day`, { params: { date } }),
  clearDay: (doctorId, data) => api.post(`/admin/doctors/${doctorId}/clear-day`, data),
  // @admin-block-range-v1 (30 Sep 2026) -- M-41. The preview changes nothing.
  // blockRange must carry expectedWindows from that preview or the backend
  // refuses with 409 STALE_PREVIEW. unblockRange removes only blocks an admin
  // made -- a doctor's own breaks are never touched.
  blockRangePreview: (doctorId, params) => api.get(`/admin/doctors/${doctorId}/block-range`, { params }),
  blockRange: (doctorId, data) => api.post(`/admin/doctors/${doctorId}/block-range`, data),
  unblockRange: (doctorId, data) => api.post(`/admin/doctors/${doctorId}/unblock-range`, data),
};

export const appointmentAPI = {
  cancelByAdmin: (appointmentId, reason) => api.post(`/admin/appointments/${appointmentId}/cancel`, { reason }),
};

// APPOINTMENTS_CALENDAR_API_2026-08-05
export const appointmentsCalendarAPI = {
  getMonth: (year, month, includeDemo = false) =>
    api.get('/admin/appointments/calendar', { params: { year, month, include_demo: includeDemo } }),
  getDay: (date, includeDemo = false) =>
    api.get('/admin/appointments/day', { params: { date, include_demo: includeDemo } }),
};

export const billingAPI = {
  getTransactions: (params) => api.get('/admin/billing', { params }),
  getStats: () => api.get('/admin/billing/stats'),
  // @generated-by docnow-d10-billing-api-merged v2
  listClearings: (params = {}) =>
    api.get('/admin/billing/clearings', { params }),
  generateClearing: (doctorId, year, month) =>
    api.post('/admin/billing/clearings/generate', { doctorId, year, month }),
  markPaid: (clearingId, { payoutDate, payoutReference }) =>
    api.patch(`/admin/billing/clearings/${clearingId}/mark-paid`, {
      payoutDate,
      payoutReference,
    }),
  // @d4-preview-wiring (16 May 2026)
  // Preview: compute clearing math for ALL active doctors for a given
  // (year, month). NO DB writes. Used by MonthlyClearingsRun page.
  previewMonth: (year, month) =>
    api.get('/admin/billing/clearings/preview', { params: { year, month } }),
  // /* @d4-billing-api-list-doctors */ D4: listClearings(params) supports year filter; bulk-generate uses generateClearing per doctor
};

export const payoutAPI = {
  getPayouts: (month, year) => api.get('/admin/payouts', { params: { month, year } }),
  approveAll: (month, year) => api.post('/admin/payouts/approve', { month, year }),
  approveSingle: (doctorId, month, year) =>
    api.post('/admin/payouts/approve-single', { doctorId, month, year }),
};

// @chronic-renewal-v1 — renewals report (counts and money only; no medicines)
// @sick-leave-v1 — money, outcomes and timings only; the endpoint returns no
// clinical content, and this is the only sick-leave call admin makes.
// @blood-tests-v1 — money, outcomes and timings only.
export const bloodTestsAdminAPI = {
    list: (params) => api.get('/blood-tests/admin/list', { params }),
};

export const sickLeaveAdminAPI = {
    list: (params) => api.get('/sick-leave/admin/list', { params }),
};

// @prep-v1 — PrEP report: status, dates and money only. The endpoint never
// returns answers, results or files; those are the claiming doctor's alone.
export const prepAdminAPI = {
    list: (params) => api.get('/prep/admin/list', { params }),
};

export const renewalAdminAPI = {
  list: (from, to) => api.get('/renewals/admin/list', { params: { from, to } }),
};

// @attendance-portal-v1
export const attendanceAPI = {
  monthly: (month) => api.get('/admin/attendance/monthly', { params: { month } }),
};

// @clinic-interest-report-v1 — anonymous tap counts per clinic, no people.
export const clinicInterestAPI = {
  list: () => api.get('/admin/clinics/interest'),
};

export const analyticsAPI = {
  getReport: (params) => api.get('/admin/analytics', { params }),
  getBookingHeatmap: (days = 90) => api.get('/admin/analytics/booking-heatmap', { params: { days } }),
};

export const serviceAPI = {
  getAll: () => api.get('/admin/services'),
  create: (data) => api.post('/admin/services', data),
  update: (id, data) => api.patch(`/admin/services/${id}`, data),
  reorder: (ids) => api.patch('/admin/services/reorder', { ids }),
};

// @clinic-pairings — which clinics a doctor delivers, and at what price.
// A pairing IS the statement that the doctor works that clinic: no pairing
// means they do not appear in that clinic's funnel and a booking against
// them is refused. Opt-in by design.
export const clinicAPI = {
  // Every clinic, with this doctor's pairing attached where one exists
  // (null otherwise) — one call gives the full checkbox state.
  listForDoctor: (doctorId) => api.get(`/admin/doctors/${doctorId}/clinics`),

  // Create or update. Requires consultationFee, commissionCents and
  // slotDurationMins — a pairing cannot exist without a price.
  upsert: (doctorId, serviceTypeId, data) =>
    api.put(`/admin/doctors/${doctorId}/clinics/${serviceTypeId}`, data),

  // Deactivates rather than deletes. Returns orphanedSlots: future
  // published slots for that clinic that become unbookable.
  deactivate: (doctorId, serviceTypeId) =>
    api.delete(`/admin/doctors/${doctorId}/clinics/${serviceTypeId}`),

  // Clinic-wide price change across every active pairing. Call without
  // confirm first to get the affected count back as a 409, then resend
  // with confirm: true.
  updateClinicPricing: (serviceTypeId, data) =>
    api.patch(`/admin/clinics/${serviceTypeId}/pricing`, data),
};

export const doctorProfileAPI = {
  getFullProfile: (doctorId) => api.get(`/admin/doctors/${doctorId}/profile`),
  updateProfileInfo: (doctorId, data) => api.patch(`/admin/doctors/${doctorId}/profile-info`, data),
  // updateStatus is the legacy /status endpoint (deleted on the backend) —
  // kept here as a stub returning a rejected promise so any stragglers in
  // the UI fail loudly with a clear error rather than silently 404-ing.
  // TODO: delete this once we're confident no caller uses it.
  updateStatus: () => Promise.reject(new Error('updateStatus is deprecated — use markLeaver/reactivate/activate')),
  markLeaver: (doctorId, data) => api.post(`/admin/doctors/${doctorId}/mark-leaver`, data),
  reactivate: (doctorId, data) => api.post(`/admin/doctors/${doctorId}/reactivate`, data),
  // Doctor onboarding lifecycle: admin clicks Activate after collecting
  // all paperwork. Backend validates everything is filled, generates a
  // setup token, and sends the welcome email.
  activate: (doctorId, force = false) => api.post(`/admin/doctors/${doctorId}/activate`, { force }),
  // For when a doctor lost the welcome email or the 7-day token expired.
  resendSetup: (doctorId) => api.post(`/admin/doctors/${doctorId}/resend-setup`),
  updateIban: (doctorId, iban) => api.patch(`/admin/doctors/${doctorId}/iban`, { iban }),
  updateMedicalAssociation: (doctorId, data) => api.patch(`/admin/doctors/${doctorId}/medical-association`, data),

  // BILLING_DETAILS_API_2026-07-06
  // Doctor billing details for Flow 1 (myDATA 2.1 τιμολόγιο counterparty).
  updateBillingDetails: (doctorId, data) =>
    api.patch(`/admin/doctors/${doctorId}/billing-details`, data),

  // RECEIPTS_ADMIN_API_2026-07-03
  // Per-doctor receipt-issuance mode ('self' | 'docnow'). Admin-only.
  // Backend guards docnow -> self with a 409 PENDING_RECEIPTS if
  // there are unresolved rows.
  setReceiptMode: (doctorId, receiptMode) =>
    api.patch(`/admin/doctors/${doctorId}/receipt-mode`, { receiptMode }),
  // Read-only hygiene rollup. Optional { year, month } narrows the
  // period; otherwise the response covers all time. Returns
  // { doctor: { receipt_mode, ... }, summary: { apy_issued, ... } }.
  getReceiptsSummary: (doctorId, params = {}) =>
    api.get(`/admin/doctors/${doctorId}/receipts/summary`, { params }),

  // addDocument uploads a file via multipart/form-data.
  // Caller must pass a FormData object containing: file (Blob), documentType, [expirationDate], [notes].
  addDocument: (doctorId, formData) => api.post(
    `/admin/doctors/${doctorId}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  updateDocument: (doctorId, docId, data) => api.patch(`/admin/doctors/${doctorId}/documents/${docId}`, data),
  deleteDocument: (doctorId, docId) => api.delete(`/admin/doctors/${doctorId}/documents/${docId}`),
  // getDocumentBlob fetches the file bytes through the backend proxy.
  // Returns the axios response; caller does URL.createObjectURL(response.data) to render in <img>.
  getDocumentBlob: (doctorId, docId) => api.get(
    `/admin/doctors/${doctorId}/documents/${docId}/file`,
    { responseType: 'blob' }
  ),
  addComplaint: (doctorId, data) => api.post(`/admin/doctors/${doctorId}/complaints`, data),
  updateComplaint: (doctorId, complaintId, data) => api.patch(`/admin/doctors/${doctorId}/complaints/${complaintId}`, data),
  addCommunication: (doctorId, data) => api.post(`/admin/doctors/${doctorId}/communications`, data),
  getExpiringDocuments: () => api.get('/admin/documents/expiring'),
};

export const patientProfileAPI = {
  getFullProfile: (patientId) => api.get(`/admin/patients/${patientId}/profile`),
  updatePatient: (patientId, data) => api.patch(`/admin/patients/${patientId}`, data),
  updateEmail: (patientId, email) => api.patch(`/admin/patients/${patientId}/email`, { email }),
  addCommunication: (patientId, data) => api.post(`/admin/patients/${patientId}/communications`, data),
  // Audit trail (GDPR Article 32)
  getAuditLogs: (patientId, params) =>
    api.get(`/admin/patients/${patientId}/audit-logs`, { params }),
  downloadAuditLogsCsv: (patientId) =>
    api.get(`/admin/patients/${patientId}/audit-logs`, {
      params: { format: 'csv', limit: 500 },
      responseType: 'blob'
    }),
  // Referral letters issued for this patient. Sorted newest first by backend.
  getReferralLetters: (patientId) =>
    api.get(`/admin/patients/${patientId}/referral-letters`),
  // Fetch the PDF bytes of a referral letter through the backend proxy.
  // Caller does URL.createObjectURL(response.data) to render or download.
  getReferralLetterPdfBlob: (letterId) =>
    api.get(`/admin/referral-letters/${letterId}/pdf`, {
      responseType: 'blob',
    }),
  // Fit notes and private prescriptions. Same shape as referral letters above.
  getFitNotes: (patientId) =>
    api.get(`/admin/patients/${patientId}/fit-notes`),
  getFitNotePdfBlob: (noteId) =>
    api.get(`/admin/fit-notes/${noteId}/pdf`, { responseType: 'blob' }),
  getPrivatePrescriptions: (patientId) =>
    api.get(`/admin/patients/${patientId}/private-prescriptions`),
  getPrivatePrescriptionPdfBlob: (prescriptionId) =>
    api.get(`/admin/private-prescriptions/${prescriptionId}/pdf`, { responseType: 'blob' }),
};

export const consultationAPI = {
  getById: (consultationId) => api.get(`/admin/consultations/${consultationId}`),
  getByAppointment: (appointmentId) => api.get(`/admin/appointments/${appointmentId}`),
};

// @admin-async-case-v1 (20 Oct 2026): the doctor's case screen for a sick-leave or
// repeat-prescription appointment. Each returns data: null when the appointment is not that kind.
export const asyncCaseAPI = {
  sickLeave: (appointmentId) => api.get(`/sick-leave/admin/by-appointment/${appointmentId}`),
  renewal: (appointmentId) => api.get(`/renewals/admin/by-appointment/${appointmentId}`),
  // @admin-patient-requests-v1: all paid requests of one patient, for the profile tabs
  sickLeaveForPatient: (patientId) => api.get(`/sick-leave/admin/patient/${patientId}`),
  renewalsForPatient: (patientId) => api.get(`/renewals/admin/patient/${patientId}`),
};

// ── Account setup (first-login password set, admin onboarding) ──
// Token-based, role-agnostic — same backend endpoint as doctor portal.
export const setupAPI = {
    validate: (token) => api.get('/auth/setup/validate', { params: { token } }),
    complete: (token, password) => api.post('/auth/setup/complete', { token, password }),
};

// ── Admin staff onboarding & management ──
// Backend mounts admin staff CRUD at /admin/admins (yes, the URL collides
// visually but the prefix is /admin and the resource is /admins).
export const adminProfileAPI = {
    getAll: () => api.get('/admin/admins'),
    create: (data) => api.post('/admin/admins', data),
    // Permanent retire — kills sessions, blocks login.
    // Backend rejects self-leaver and last-active-admin.
    markLeaver: (adminId, data) => api.post(`/admin/admins/${adminId}/mark-leaver`, data),
    // @admin-update-method
    // Edit name/phone/IBAN. Email is the fixed login identity (not editable).
    update: (adminId, data) => api.put(`/admin/admins/${adminId}`, data),
    // Re-issue welcome email if original was lost or 7-day token expired.
    // Backend rejects if password already set OR admin is leaver.
    resendSetup: (adminId) => api.post(`/admin/admins/${adminId}/resend-setup`),
};

// ----------------------------------------------------------------------
/* @d4-doctors-api */
// D4: admin list of all doctors with isLeaver / commissionCents (extended May 11)
// ----------------------------------------------------------------------
export const doctorsAPI = {
  list: () => api.get('/admin/doctors'),
};


// PLATFORM_CONFIG_API_2026-07-06
export const platformConfigAPI = {
  get:    ()     => api.get("/admin/platform-config"),
  update: (body) => api.put("/admin/platform-config", body),
};


// DOCTOR_INVOICES_API_2026-07-06
export const doctorInvoicesAPI = {
  listMonth: (year, month) => api.get(`/admin/doctor-invoices/month/${year}/${month}`),
  issue:     (body)         => api.post("/admin/doctor-invoices/issue", body),
};

// @discount-codes-portal-v1
export const discountAPI = {
  list:          ()            => api.get('/admin/discount-codes'),
  create:        (payload)     => api.post('/admin/discount-codes', payload),
  setActive:     (id, isActive)=> api.patch(`/admin/discount-codes/${id}`, { isActive }),
  redemptions:   (id)          => api.get(`/admin/discount-codes/${id}/redemptions`),
  waiverDoctors: ()            => api.get('/admin/discount-codes/waiver-doctors'),
  // @discount-percent-v1 - live fee/commission points, so the form can
  // show what a code is really worth before it is created.
  pricingImpact: ()             => api.get('/admin/discount-codes/pricing-impact'),
};
